import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEdgesState, useNodesState, type Node } from '@xyflow/react';
import { TipoAlvo, type AlvoDto } from '@nexital/shared';
import { useColaboracao, useDependencias, useQuadro } from '../../application/dependencias';
import type { AlvoNoQuadro, Ponto } from '../../domain/notas';
import { useNotas } from '../notas/contexto-notas';
import { manterSelecao, montarNosNotas, type ArestaSetaTipo, type NoNotaTipo } from '../notas/montar-nos-notas';
import { aplicarColaboracao } from './colaboracao-nos';
import { montarNos, uuidDoNo, type NoQuadro } from './montar-nos';

const DURACAO_VOLTA_MS = 450;

export type NoDoQuadro = NoQuadro | NoNotaTipo;

/** Posição absoluta de cada grupo e cartão (cartões são filhos do grupo, com posição relativa). */
function retangulos(nos: NoQuadro[]): AlvoNoQuadro[] {
  const porId = new Map(nos.map((n) => [n.id, n]));
  return nos.map((n) => {
    const pai = n.parentId ? porId.get(n.parentId) : undefined;
    const x = n.position.x + (pai?.position.x ?? 0);
    const y = n.position.y + (pai?.position.y ?? 0);
    const alvo: AlvoDto = { tipo: n.type === 'grupo' ? TipoAlvo.Grupo : TipoAlvo.Processo, uuid: uuidDoNo(n.id) };
    return { x, y, largura: n.width ?? 0, altura: n.height ?? 0, alvo };
  });
}

/** Nós sendo arrastados aqui ficam como estão, para a releitura não puxar o que está na mão da pessoa. */
function preservarArrastados<N extends Node>(novos: N[]) {
  return (atuais: N[]) => {
    const naMao = new Map(atuais.filter((n) => n.dragging).map((n) => [n.id, n]));
    return novos.map((n) => naMao.get(n.id) ?? n);
  };
}

/**
 * Nós e arestas do quadro: grupos e cartões (montar-nos), notas e setas (RQ-NOT-003, RQ-NOT-004) e, por cima,
 * o que os colegas estão fazendo (RQ-COL-002, RQ-COL-004). `voltar` devolve um cartão à origem com animação.
 */
export function useNosQuadro() {
  const { sessao } = useDependencias();
  const dados = useQuadro((s) => s.dados);
  const nivel = useQuadro((s) => s.nivel);
  const notas = useNotas((s) => s.notas);
  const setas = useNotas((s) => s.setas);
  const arrastes = useColaboracao((s) => s.arrastes);
  const travas = useColaboracao((s) => s.travas);
  const [nos, definirNos, aoMudarNos] = useNodesState<NoDoQuadro>([]);
  const [arestas, definirArestas, aoMudarArestas] = useEdgesState<ArestaSetaTipo>([]);
  const [voltando, definirVoltando] = useState<string | null>(null);

  const voltar = useCallback((idNo: string) => {
    definirVoltando(idNo);
    window.setTimeout(() => definirVoltando(null), DURACAO_VOLTA_MS);
  }, []);

  const doQuadro = useMemo(() => (dados ? montarNos(dados, nivel, voltando) : []), [dados, nivel, voltando]);
  const alvos = useMemo(() => retangulos(doQuadro), [doQuadro]);

  useEffect(() => {
    const posicaoDoAlvo = (alvo: AlvoDto): Ponto | null => alvos.find((a) => a.alvo.uuid === alvo.uuid) ?? null;
    const { nos: deNotas, arestas: deSetas } = montarNosNotas(notas, setas, posicaoDoAlvo);
    const todos = aplicarColaboracao<NoDoQuadro>([...doQuadro, ...deNotas], { eu: sessao.usuarioUuid, arrastes, travas });
    definirNos(preservarArrastados(todos));
    definirArestas(manterSelecao(deSetas));
  }, [doQuadro, alvos, notas, setas, arrastes, travas, sessao.usuarioUuid, definirNos, definirArestas]);

  return { nos, arestas, aoMudarNos, aoMudarArestas, voltar, alvos };
}
